import { useEffect, useRef, useState } from "react";

import FinalJeopardy from "./FinalJeopardy";
import GameLoader from "./GameLoader";
import GameOver from "./GameOver";
import JeopardyBoard from "./JeopardyBoard";
import PlayerChooser from "./PlayerChooser";
import Scoreboard from "./Scoreboard";
import BuzzerStatus, { BuzzerState } from "./BuzzerStatus";
import InviteOverlay from "./InviteOverlay";
import {
  Game,
  GameData,
  GameRound,
  Player,
  RoundName,
  ROUND_SINGLE,
} from "../types";
import { logEvent, logEventWithLabel } from "../util/analytics";
import { preloadedGames } from "../util/preloaded_games";
import { useGameSocket } from "../hooks/useGameSocket";
import gamy from "../games/gamy.json";

import "./App.css";

// Local fallback if the server hasn't yet broadcast its gameData — the
// server's persisted copy in data/jeopardy-game.json is authoritative once
// it arrives.
const INITIAL_GAME = (gamy as unknown as GameData).game;

const SFX_URLS = {
  intro: '/sounds/jeopardy-intro.mp3',
  theme: '/sounds/jeopardy-theme.mp3',
  'daily-double': '/sounds/daily-double.mp3',
  'right-answer': '/sounds/right-answer.mp3',
  'board-fill': '/sounds/board-fill.mp3',
  'times-up': '/sounds/times-up.mp3',
  outro: '/sounds/outro.mp3',
};
const INITIAL_PLAYERS: Player[] = [
  { name: "Team One", score: 0, correct: 0, incorrect: 0 },
  { name: "Team Two", score: 0, correct: 0, incorrect: 0 },
  { name: "Team Three", score: 0, correct: 0, incorrect: 0 },
];

function deepCloneGame(g: Game): Game {
  return JSON.parse(JSON.stringify(g));
}

function App() {
  const [game, setGame] = useState<Game>(deepCloneGame(INITIAL_GAME));

  // Replace the local game structure with a server-authoritative copy while
  // preserving "chosen" marks position-by-position so an admin edit mid-game
  // doesn't reset which clues have been played.
  function applyServerGameData(serverGame: Game) {
    setGame(prev => {
      const next = deepCloneGame(serverGame);
      for (const r of ['single', 'double'] as const) {
        const oldR = (prev as any)[r];
        const newR = (next as any)[r];
        if (!oldR || !newR) continue;
        for (let ci = 0; ci < newR.length; ci++) {
          for (let qi = 0; qi < (newR[ci].clues || []).length; qi++) {
            if (oldR[ci]?.clues?.[qi]?.chosen) newR[ci].clues[qi].chosen = true;
          }
        }
      }
      return next;
    });
  }

  const [players, setPlayers] = useState<Player[]>(INITIAL_PLAYERS);
  const [round, setRound] = useState<RoundName>(ROUND_SINGLE);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [numCategoriesShown, setNumCategoriesShown] = useState(0);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState<number | null>(null);
  const [currentClueIndex, setCurrentClueIndex] = useState<number | null>(null);
  const [buzzer, setBuzzer] = useState<BuzzerState | null>(null);
  const [buzzerCount, setBuzzerCount] = useState<number[]>([0, 0, 0]);
  const [ddWager, setDdWager] = useState<{ teamIndex: number; amount: number } | null>(null);
  const [fjPhase, setFjPhase] = useState<string | null>(null);
  const [fjWagers, setFjWagers] = useState<(number | null)[]>([null, null, null]);
  const [fjAnswers, setFjAnswers] = useState<(string | null)[]>([null, null, null]);
  const [fjReviewIndex, setFjReviewIndex] = useState<number>(0);
  const [fjReviewShowWager, setFjReviewShowWager] = useState<boolean>(false);
  const [fjReviewResults, setFjReviewResults] = useState<(string | null)[]>([null, null, null]);
  const [rosters, setRosters] = useState<string[][]>([[], [], []]);
  const [captains, setCaptains] = useState<(string | null)[]>([null, null, null]);
  const [invitePanel, setInvitePanel] = useState<{ open: boolean; teamIndex: number }>({ open: false, teamIndex: 0 });

  // Daily Double sound — plays on the board when a DD clue is selected.
  const ddSoundRef = useRef<HTMLAudioElement>(null);
  const prevCellRef = useRef<{ cat: number | null; clue: number | null }>({ cat: null, clue: null });
  // Right-answer ding — plays when answered transitions true AND any team's
  // score increased (covers both buzz-correct and DD-correct).
  const rightSoundRef = useRef<HTMLAudioElement>(null);
  const prevScoreSumRef = useRef<number>(0);
  const prevAnsweredRef = useRef<boolean>(false);
  // Board-fill swoosh — plays when category reveals finish and the grid appears.
  const boardFillSoundRef = useRef<HTMLAudioElement>(null);
  const prevCatsShownRef = useRef<number>(0);

  // Refs track server-authoritative values so the WS handler doesn't go stale
  const hasRestored = useRef(false);
  const roundRef = useRef<string>(ROUND_SINGLE);
  const gameStartedRef = useRef(false);

  // ── WebSocket: restore on first STATE, sync on every subsequent STATE ────
  const { send: sendWS } = useGameSocket((msg) => {
    if (msg.type === "SFX") {
      // Host-triggered sound effect — play on the board.
      const url = SFX_URLS[msg.name as keyof typeof SFX_URLS];
      if (url) {
        const audio = new Audio(url);
        audio.volume = 0.7;
        audio.play().catch(() => { /* autoplay blocked — ignore */ });
      }
      return;
    }
    if (msg.type !== "STATE") return;

    // ── First STATE: full restore after page load / reconnect ──────────────
    if (!hasRestored.current) {
      hasRestored.current = true;

      if (Array.isArray(msg.teams)) {
        setPlayers(INITIAL_PLAYERS.map((p, i) => ({
          ...p,
          score: msg.teams[i]?.score ?? 0,
        })));
      }

      const restoredRound = (msg.round as RoundName) || ROUND_SINGLE;
      setRound(restoredRound);
      roundRef.current = restoredRound;

      const restoredStarted = !!msg.gameStarted;
      setIsGameStarted(restoredStarted);
      gameStartedRef.current = restoredStarted;

      // Restore chosen clues — mark played cells on the board
      if (Array.isArray(msg.chosenClues) && msg.chosenClues.length > 0) {
        setGame(prev => {
          const g = deepCloneGame(prev);
          (msg.chosenClues as Array<{ round: string; categoryIndex: number; clueIndex: number }>)
            .forEach(({ round: r, categoryIndex, clueIndex }) => {
              const cell = (g as any)[r]?.[categoryIndex]?.clues?.[clueIndex];
              if (cell) cell.chosen = true;
            });
          return g;
        });
        setNumCategoriesShown(9999); // skip category-reveal animations
      }

      // Restore active clue indices so the clue display reappears correctly
      if (msg.activeClueMeta) {
        const meta = msg.activeClueMeta;
        setCurrentCategoryIndex(meta.categoryIndex);
        setCurrentClueIndex(meta.clueIndex);
      }

      if (msg.buzzer) setBuzzer(msg.buzzer);
      if (Array.isArray(msg.buzzerCount)) setBuzzerCount(msg.buzzerCount);
      if ('ddWager' in msg) setDdWager(msg.ddWager ?? null);
      if ('fjPhase' in msg) setFjPhase(msg.fjPhase ?? null);
      if (Array.isArray(msg.fjWagers)) setFjWagers(msg.fjWagers);
      if (Array.isArray(msg.fjAnswers)) setFjAnswers(msg.fjAnswers);
      if ('fjReviewIndex' in msg) setFjReviewIndex(msg.fjReviewIndex ?? 0);
      if ('fjReviewShowWager' in msg) setFjReviewShowWager(!!msg.fjReviewShowWager);
      if (Array.isArray(msg.fjReviewResults)) setFjReviewResults(msg.fjReviewResults);
      if (Array.isArray(msg.rosters)) setRosters(msg.rosters);
      if (Array.isArray(msg.captains)) setCaptains(msg.captains);
      if (msg.invitePanel) setInvitePanel(msg.invitePanel);
      if (msg.gameData?.game) applyServerGameData(msg.gameData.game);
      return;
    }

    // ── Subsequent STATEs: incremental sync ────────────────────────────────

    if (Array.isArray(msg.teams)) {
      setPlayers(prev => prev.map((p, i) => ({
        ...p,
        score: msg.teams[i]?.score ?? p.score,
      })));
    }

    if (msg.buzzer !== undefined) setBuzzer(msg.buzzer);
    if (Array.isArray(msg.buzzerCount)) setBuzzerCount(msg.buzzerCount);
    if ('ddWager' in msg) setDdWager(msg.ddWager ?? null);
    if ('fjPhase' in msg) setFjPhase(msg.fjPhase ?? null);
    if (Array.isArray(msg.fjWagers)) setFjWagers(msg.fjWagers);
    if (Array.isArray(msg.fjAnswers)) setFjAnswers(msg.fjAnswers);
    if ('fjReviewIndex' in msg) setFjReviewIndex(msg.fjReviewIndex ?? 0);
    if ('fjReviewShowWager' in msg) setFjReviewShowWager(!!msg.fjReviewShowWager);
    if (Array.isArray(msg.fjReviewResults)) setFjReviewResults(msg.fjReviewResults);
    if (Array.isArray(msg.rosters)) setRosters(msg.rosters);
    if (Array.isArray(msg.captains)) setCaptains(msg.captains);
    if (msg.invitePanel) setInvitePanel(msg.invitePanel);
    if (msg.gameData?.game) applyServerGameData(msg.gameData.game);

    // Detect host-driven round change (SET_ROUND from host page)
    const serverRound = msg.round as RoundName;
    if (serverRound && serverRound !== roundRef.current) {
      roundRef.current = serverRound;
      setRound(serverRound);
      setCurrentCategoryIndex(null);
      setCurrentClueIndex(null);
      setNumCategoriesShown(0);
    }

    // Detect clue dismissed by host — reset local indices when server clears activeClueMeta
    if (!msg.activeClueMeta) {
      setCurrentCategoryIndex(null);
      setCurrentClueIndex(null);
    }

    // Handle forced clue from host board — override game screen to show specific clue
    if (msg.activeClueMeta?.forced === true) {
      const meta = msg.activeClueMeta;
      // Ensure game is showing the board (not splash)
      if (!gameStartedRef.current) {
        gameStartedRef.current = true;
        setIsGameStarted(true);
      }
      // Switch round if needed
      if (meta.roundName !== roundRef.current) {
        roundRef.current = meta.roundName;
        setRound(meta.roundName);
      }
      // Always skip category reveals when jumping directly to a clue
      setNumCategoriesShown(9999);
      setCurrentCategoryIndex(meta.categoryIndex);
      setCurrentClueIndex(meta.clueIndex);
      setGame(prev => {
        const g = deepCloneGame(prev);
        const cell = (g as any)[meta.roundName]?.[meta.categoryIndex]?.clues?.[meta.clueIndex];
        if (cell) cell.chosen = true;
        return g;
      });
    }

    // Detect game reset (host pressed New Game)
    const serverGameStarted = !!msg.gameStarted;
    if (serverGameStarted !== gameStartedRef.current) {
      gameStartedRef.current = serverGameStarted;
      setIsGameStarted(serverGameStarted);
      if (!serverGameStarted) {
        // Full UI reset — clear chosen flags, return to splash
        setGame(deepCloneGame(INITIAL_GAME));
        setPlayers(INITIAL_PLAYERS);
        setRound(ROUND_SINGLE);
        roundRef.current = ROUND_SINGLE;
        setCurrentCategoryIndex(null);
        setCurrentClueIndex(null);
        setNumCategoriesShown(0);
        setFjPhase(null);
        setFjWagers([null, null, null]);
      }
    }
  });

  // ── Preloaded game via ?game= query param ─────────────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gameId = urlParams.get("game");
    if (gameId === null) return;
    const preloadedGame: GameData | undefined = (preloadedGames as any)[gameId];
    if (preloadedGame === undefined) return;
    window.history.replaceState({}, document.title, "/");
    logEventWithLabel("Load Preloaded Game", gameId);
    updateGame(preloadedGame);
  }, []);

  function updateGame(data: GameData) {
    setPlayers(data.players || INITIAL_PLAYERS);
    const newRound = data.round || ROUND_SINGLE;
    setRound(newRound);
    roundRef.current = newRound;
    const started = data.players !== undefined;
    setIsGameStarted(started);
    gameStartedRef.current = started;
    setGame(data.game);
    // Tell the server the game has started so STATE syncs correctly
    if (started) sendWS({ type: "GAME_STARTED" });
  }

  function addPlayer(name: string) {
    logEvent("Add Player");
    setPlayers([...players, { name, score: 0, correct: 0, incorrect: 0 }]);
  }

  function playGame() {
    logEvent("Play Game");
    gameStartedRef.current = true;
    setIsGameStarted(true);
    sendWS({ type: "GAME_STARTED" });
  }

  function handleCategoryShown() {
    logEvent("Show Category");
    setNumCategoriesShown(numCategoriesShown + 1);
  }

  function chooseClue(categoryIndex: number, clueIndex: number) {
    logEvent("Show Clue");
    const newGame: Game = Object.assign({}, game);
    const newRound: GameRound = (newGame as any)[round];
    newRound[categoryIndex].clues[clueIndex].chosen = true;
    setGame(newGame);
    setCurrentCategoryIndex(categoryIndex);
    setCurrentClueIndex(clueIndex);

    const category = (game as any)[round][categoryIndex];
    const clue = category.clues[clueIndex];
    sendWS({
      type: "CLUE_SELECTED",
      roundName: round,
      categoryIndex,
      clueIndex,
      category: category.category,
      value: clue.value,
      clue: clue.clue,
      answer: clue.solution,
      isDailyDouble: !!clue.dailyDouble,
    });
  }

  function updateScore(playerIndex: number, value: number, correct: boolean) {
    logEvent("Update Score");
    const newPlayers = [...players];
    newPlayers[playerIndex] = {
      ...newPlayers[playerIndex],
      score: newPlayers[playerIndex].score + value,
      correct: newPlayers[playerIndex].correct + (correct ? 1 : 0),
      incorrect: newPlayers[playerIndex].incorrect + (correct ? 0 : 1),
    };
    setPlayers(newPlayers);
    sendWS({ type: "SCORE_ADJUST", teamIndex: playerIndex, delta: value });
  }

  function returnToBoard() {
    logEvent("Back to Board");
    setCurrentClueIndex(null);
    setCurrentCategoryIndex(null);
    sendWS({ type: "CLUE_DISMISSED" });
  }

  function proceedToDouble() {
    logEvent("Proceed to Double Jeopardy");
    roundRef.current = "double";
    setNumCategoriesShown(0);
    setRound("double");
    sendWS({ type: "ROUND_CHANGED", round: "double" });
  }

  function proceedToFinal() {
    logEvent("Proceed to Final Jeopardy");
    roundRef.current = "final";
    setRound("final");
    sendWS({ type: "ROUND_CHANGED", round: "final" });
  }

  function finishGame() {
    logEvent("Finish Game");
    roundRef.current = "done";
    setRound("done");
    sendWS({ type: "ROUND_CHANGED", round: "done" });
  }

  function handleBuzzResult(correct: boolean) {
    sendWS({ type: "BUZZ_RESULT", correct });
  }

  // Answer text for BuzzerStatus reveal
  // Play the DD sound on the board when a Daily Double clue is selected.
  useEffect(() => {
    const prev = prevCellRef.current;
    const isNewCell =
      currentCategoryIndex !== null && currentClueIndex !== null &&
      (prev.cat !== currentCategoryIndex || prev.clue !== currentClueIndex);
    if (isNewCell) {
      const clue = (game as any)[round]?.[currentCategoryIndex!]?.clues?.[currentClueIndex!];
      if (clue?.dailyDouble && ddSoundRef.current) {
        ddSoundRef.current.currentTime = 0;
        ddSoundRef.current.volume = 0.7;
        ddSoundRef.current.play().catch(() => { /* autoplay blocked — ignore */ });
      }
    }
    prevCellRef.current = { cat: currentCategoryIndex, clue: currentClueIndex };
  }, [currentCategoryIndex, currentClueIndex, round, game]);

  // Right-answer ding when any team's score just increased AND the buzzer
  // just transitioned to answered (catches both regular-buzz-correct and
  // DD-correct; ignores reveal-bypass and all-wrong-reveal where total
  // score is unchanged).
  useEffect(() => {
    const newSum = players.reduce((s, p) => s + p.score, 0);
    const justAnswered = !!buzzer?.answered && !prevAnsweredRef.current;
    if (justAnswered && newSum > prevScoreSumRef.current && rightSoundRef.current) {
      rightSoundRef.current.currentTime = 0;
      rightSoundRef.current.volume = 0.7;
      rightSoundRef.current.play().catch(() => {});
    }
    prevScoreSumRef.current = newSum;
    prevAnsweredRef.current = !!buzzer?.answered;
  }, [players, buzzer]);

  // Board-fill swoosh when the per-round category reveal finishes and the
  // grid appears.
  useEffect(() => {
    if (round !== 'single' && round !== 'double') return;
    const boardForRound = (game as any)[round];
    const length: number = boardForRound?.length ?? 6;
    if (prevCatsShownRef.current < length && numCategoriesShown >= length) {
      if (boardFillSoundRef.current) {
        boardFillSoundRef.current.currentTime = 0;
        boardFillSoundRef.current.volume = 0.7;
        boardFillSoundRef.current.play().catch(() => {});
      }
    }
    prevCatsShownRef.current = numCategoriesShown;
  }, [numCategoriesShown, round, game]);

  const activeAnswer: string = (() => {
    if (currentCategoryIndex === null || currentClueIndex === null) return "";
    const board = (game as any)[round];
    return board?.[currentCategoryIndex]?.clues?.[currentClueIndex]?.solution ?? "";
  })();

  // ── Render ─────────────────────────────────────────────────────────────────

  const inviteOverlay = invitePanel.open ? (
    <InviteOverlay
      team={invitePanel.teamIndex}
      teams={players}
      rosters={rosters}
      captains={captains}
      origin={window.location.origin}
      interactive={false}
    />
  ) : null;

  if (!isGameStarted) {
    return (
      <div className="app">
        <GameLoader />
        {inviteOverlay}
        <audio ref={ddSoundRef} src="/sounds/daily-double.mp3" preload="auto" />
        <audio ref={rightSoundRef} src="/sounds/right-answer.mp3" preload="auto" />
        <audio ref={boardFillSoundRef} src="/sounds/board-fill.mp3" preload="auto" />
      </div>
    );
  }

  if (round === "single" || round === "double") {
    const board = game[round];
    if (board === undefined) {
      return <div>Error: Game board not found.</div>;
    }

    let allowProceedToDouble = round === "single";
    if (allowProceedToDouble) {
      board.forEach(cat => cat.clues.forEach(clue => {
        if (clue.chosen === undefined) allowProceedToDouble = false;
      }));
    }

    let allowProceedToFinal = round === "double";
    if (allowProceedToFinal) {
      board.forEach(cat => cat.clues.forEach(clue => {
        if (clue.chosen === undefined) allowProceedToFinal = false;
      }));
    }

    if (allowProceedToDouble && game.double === undefined) {
      allowProceedToDouble = false;
      allowProceedToFinal = true;
    }

    const buzzersArmed = !!buzzer?.armed && !buzzer?.activeBuzz;

    return (
      <div className={`app${buzzersArmed ? ' app-armed' : ''}`}>
        <div className="board-wrap">
          <JeopardyBoard
            board={board}
            backToBoard={returnToBoard}
            categoryShown={handleCategoryShown}
            chooseClue={chooseClue}
            categoriesShown={numCategoriesShown}
            currentCategory={currentCategoryIndex}
            currentClue={currentClueIndex}
            solutionRevealed={!!buzzer?.answered}
            ddShown={!!buzzer?.ddShown}
            ddWager={ddWager}
          />
        </div>

        {buzzer && (
          <BuzzerStatus
            buzzer={buzzer}
            answer={activeAnswer}
          />
        )}

        <Scoreboard
          players={players}
          currentValue={
            currentCategoryIndex !== null && currentClueIndex !== null
              ? board[currentCategoryIndex].clues[currentClueIndex].value
              : null
          }
          updateScore={updateScore}
          wagering={
            currentCategoryIndex !== null &&
            currentClueIndex !== null &&
            board[currentCategoryIndex].clues[currentClueIndex].dailyDouble === true
          }
          stats={false}
          buzzer={buzzer}
          buzzerCount={buzzerCount}
          rosters={rosters}
          captains={captains}
        />
        {inviteOverlay}
        <audio ref={ddSoundRef} src="/sounds/daily-double.mp3" preload="auto" />
        <audio ref={rightSoundRef} src="/sounds/right-answer.mp3" preload="auto" />
        <audio ref={boardFillSoundRef} src="/sounds/board-fill.mp3" preload="auto" />
      </div>
    );
  }

  if (round === "final") {
    return (
      <div className="app">
        <div className="board-wrap">
          <FinalJeopardy
            final={game.final}
            onFinishGame={finishGame}
            fjPhase={fjPhase}
            fjAnswers={fjAnswers}
            fjWagers={fjWagers}
            fjReviewIndex={fjReviewIndex}
            fjReviewShowWager={fjReviewShowWager}
            fjReviewResults={fjReviewResults}
            players={players}
          />
        </div>
        <Scoreboard
          players={players}
          currentValue={0}
          updateScore={updateScore}
          wagering={true}
          stats={false}
          rosters={rosters}
          captains={captains}
        />
        {inviteOverlay}
        <audio ref={ddSoundRef} src="/sounds/daily-double.mp3" preload="auto" />
        <audio ref={rightSoundRef} src="/sounds/right-answer.mp3" preload="auto" />
        <audio ref={boardFillSoundRef} src="/sounds/board-fill.mp3" preload="auto" />
      </div>
    );
  }

  if (round === "done") {
    return (
      <div className="app">
        <GameOver players={players} />
        {inviteOverlay}
        <audio ref={ddSoundRef} src="/sounds/daily-double.mp3" preload="auto" />
        <audio ref={rightSoundRef} src="/sounds/right-answer.mp3" preload="auto" />
        <audio ref={boardFillSoundRef} src="/sounds/board-fill.mp3" preload="auto" />
      </div>
    );
  }

  return <div>Error: Unknown game round.</div>;
}

export default App;
